/**
 * Shared Axios Instance with Interceptors
 * This instance is configured with request/response interceptors for:
 * - Request ID tracking
 * - Session ID management
 * - Authentication token handling
 * - Error handling and logging
 * 
 * Used by both existing API services and generated OpenAPI clients
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse, AxiosError } from 'axios';
import { generateRequestId } from '../utils/requestId';
import { getApiBaseUrl } from '../utils/env';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore -- plain JS module, no types
import { getEmbedAuth, isEmbedMode } from '../utils/embedAuth';

// Base URL for API calls
// In development, requests will be proxied to http://localhost:5000
// In production, use the environment variable (injected at runtime via env-config.js)
const API_BASE_URL = getApiBaseUrl();

/**
 * Sets up request and response interceptors on an axios instance
 * @param instance - The axios instance to configure
 */
export function setupAxiosInterceptors(instance: AxiosInstance): void {
  // Request interceptor for adding request ID, session ID, and auth tokens
  instance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Generate and add request ID for tracking
      const requestId = generateRequestId();
      if (config.headers) {
        config.headers['X-Request-ID'] = requestId;
      }
      
      // Add session ID if available
      const sessionId = localStorage.getItem('sessionId');
      if (sessionId && config.headers) {
        config.headers['X-Session-ID'] = sessionId;
      }
      
      // Log request initiation
      console.log(`[${requestId}] ${config.method?.toUpperCase()} ${config.url}`);
      
      // Add auth token if available
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // Embed-mode (e.g. dm.cerebralmachines.ai iframing the chat) injects
      // a public embed token + the partner-account id so the backend's
      // session middleware can synthesize a limited session without
      // requiring a CM login.
      const embed = getEmbedAuth();
      if (embed && embed.token && config.headers) {
        config.headers['X-CM-Embed-Token'] = embed.token;
        if (embed.accountId && !config.headers['X-CM-Account-Id']) {
          config.headers['X-CM-Account-Id'] = embed.accountId;
        }
      }
      return config;
    },
    (error: AxiosError) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor for logging and error handling
  instance.interceptors.response.use(
    (response: AxiosResponse) => {
      // Log successful response with request ID
      const requestId = response.config.headers?.['X-Request-ID'] as string || 
                       response.headers['x-request-id'] || 
                       'unknown';
      console.log(`[${requestId}] Response: ${response.status}`);
      return response;
    },
    (error: AxiosError) => {
      // Get request ID for error tracking
      const requestId = (error.config?.headers?.['X-Request-ID'] as string) || 
                       (error.response?.headers?.['x-request-id'] as string) || 
                       'unknown';
      
      if (error.response) {
        // Server responded with error status
        console.error(`[${requestId}] API Error ${error.response.status}:`, error.response.data);
        
        // Handle 402 Payment Required - redirect to pricing/upgrade page
        if (error.response.status === 402) {
          const responseData = error.response.data as any;
          if (responseData?.upgradeRequired) {
            const currentPath = window.location.pathname;
            if (!currentPath.startsWith('/user-home/pricing') && !currentPath.startsWith('/user-home/checkout')) {
              // Store the upgrade message for the pricing page to display
              sessionStorage.setItem('upgradeReason', responseData?.error || 'Please upgrade your plan.');
              window.location.href = '/user-home/pricing';
            }
          }
        }

        // Handle 401 Unauthorized - clear auth and session data and redirect to login
        if (error.response.status === 401) {
          // Check if it's a session expiry error
          const responseData = error.response.data as any;
          if (responseData?.error === 'Valid session required') {
            localStorage.removeItem('sessionId');
          }
          localStorage.removeItem('authToken');
          localStorage.removeItem('userData');
          localStorage.removeItem('user');
          localStorage.removeItem('token');
          // Only redirect to login if not already on auth pages -- and
          // never bounce the embed iframe to /login (that would yank the
          // CM brand chrome into the partner subdomain, which is exactly
          // what the embed flow exists to avoid).
          const currentPath = window.location.pathname;
          const isEmbedRoute = currentPath.startsWith('/embed/');
          if (!isEmbedMode() && !isEmbedRoute
              && !['/login', '/signup', '/forgot-password', '/reset-password', '/activate'].includes(currentPath)) {
            window.location.href = '/login';
          }
        }
      } else if (error.request) {
        // Request made but no response
        console.error(`[${requestId}] Network Error:`, error.message);
      } else {
        // Something else happened
        console.error(`[${requestId}] Error:`, error.message);
      }
      return Promise.reject(error);
    }
  );
}

/**
 * Creates a configured axios instance with interceptors
 * @returns {AxiosInstance} Configured axios instance
 */
export function createAxiosInstance(): AxiosInstance {
  const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    timeout: 120000, // 120 second timeout – document operations (chunk, process, upload) can be slow
  });

  // Set up interceptors
  setupAxiosInterceptors(instance);

  return instance;
}

// Export a singleton instance for use across the application
export const axiosInstance = createAxiosInstance();

